import { ReactNode, createContext, useContext, useState } from 'react'
import { useAuth } from './AuthContext'

interface SecurityContextType {
  scopes: Record<string, unknown>
  setScopes: (scopes: Record<string, unknown>) => void
  getScopes: () => Promise<Record<string, unknown>>
  handleLogout: () => void
  hasGravitateAccess: () => boolean
  isGravitateAdmin: () => boolean
}

const SecurityContext = createContext<SecurityContextType | undefined>(
  undefined,
)

export const SecurityContextProvider = ({
  children,
}: { children: ReactNode }) => {
  const { user, logout } = useAuth()
  const [scopes, setScopes] = useState<Record<string, unknown>>({})

  function hasGravitateAccess(): boolean {
    return user?.hasGravitateAccess || false
  }

  /**
   * Mirrors the backend's gravitate_admin_access: a member of the Gravitate
   * tenant holding the admin role there.
   */
  function isGravitateAdmin(): boolean {
    return (
      user?.domains?.some(
        (domain) =>
          domain.tenant_name === 'Gravitate' && domain.roles.includes('admin'),
      ) ?? false
    )
  }

  const getScopes = async (): Promise<Record<string, unknown>> => {
    const hasGravitatePermission = hasGravitateAccess()

    // Flatten the user's JWT scopes into boolean flags so page hasPermission
    // checks (e.g. hasScope(scopes, 'tenant_configuration')) can read them.
    const userScopeList = user?.scopes ?? []
    const userScopes = Object.fromEntries(
      userScopeList.map((scope) => [scope, true]),
    )

    if (hasGravitatePermission) {
      return {
        operations: { view: true },
        dashboard: { view: true },
        network: { view: true },
        crossroads: { view: true },
        // Tools is the only Gravitate section gated on a scope, so it is
        // conditional here -- a section's own hasPermission is never called.
        ...(userScopeList.includes('use_tools')
          ? { tools: { view: true } }
          : {}),
        ...userScopes,
        ...scopes,
      }
    } else {
      return {
        dashboard: { view: true },
        // A top-level section only renders when its key is present here, so
        // reveal Operations when the user can manage orders.
        ...(userScopeList.includes('order_management')
          ? { operations: { view: true } }
          : {}),
        ...userScopes,
        ...scopes,
      }
    }
  }

  const handleLogout = () => {
    logout()
    setScopes({})
    window.location.href = '/login'
  }

  return (
    <SecurityContext.Provider
      value={{
        scopes,
        setScopes,
        getScopes,
        handleLogout,
        hasGravitateAccess,
        isGravitateAdmin,
      }}
    >
      {children}
    </SecurityContext.Provider>
  )
}

export const useSecurityContext = () => {
  const context = useContext(SecurityContext)
  if (!context) {
    throw new Error(
      'useSecurityContext must be used within a SecurityContextProvider',
    )
  }
  return context
}
